package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.qrkontaq.ui.map.CARTO_VOYAGER_TILES
import com.qrkontaq.ui.map.ensureLeaflet
import com.qrkontaq.ui.tenant.InlineIcon
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px

private const val MAP_ID = "qk-adm-map"

/**
 * Interactive OpenStreetMap (Leaflet) picker with a fixed centre pin: the owner pans the map
 * and the marker stays centred, so the map centre is the chosen point. Defaults to the saved
 * coordinates, otherwise to the browser's current geolocation. No API key required.
 */
@Composable
internal fun MapPicker(
    lat: Double?,
    lng: Double?,
    onPick: (Double, Double) -> Unit,
) {
    LaunchedEffect(Unit) {
        ensureLeaflet()
        initMap(lat, lng, onPick)
    }

    Column(Modifier.fillMaxWidth().margin(top = 4.px, bottom = 6.px)) {
        Box(
            Modifier
                .fillMaxWidth()
                .height(220.px)
                .borderRadius(12.px)
                .border(1.px, LineStyle.Solid, Brand.Divider)
                .backgroundColor(Brand.WarmBackground)
                .position(org.jetbrains.compose.web.css.Position.Relative)
                .styleModifier { property("overflow", "hidden") },
        ) {
            // Leaflet renders into this element; keep it childless so Compose never fights it.
            Box(Modifier.fillMaxSize().attrsModifier { attr("id", MAP_ID) })

            // Fixed centre pin — its tip sits exactly on the map centre.
            Box(
                Modifier.styleModifier {
                    property("position", "absolute")
                    property("left", "50%")
                    property("top", "50%")
                    property("transform", "translate(-50%, -100%)")
                    property("pointer-events", "none")
                    property("z-index", "500")
                    property("filter", "drop-shadow(0 3px 4px rgba(0,0,0,.3))")
                },
            ) {
                InlineIcon(AdminIcons.PinSolid, 40, "#0F9D6E")
            }
            // Small ground dot at the precise centre.
            Box(
                Modifier.styleModifier {
                    property("position", "absolute")
                    property("left", "50%")
                    property("top", "50%")
                    property("transform", "translate(-50%, -50%)")
                    property("width", "8px")
                    property("height", "8px")
                    property("border-radius", "999px")
                    property("background", "rgba(15,157,110,.9)")
                    property("box-shadow", "0 0 0 3px rgba(15,157,110,.25)")
                    property("pointer-events", "none")
                    property("z-index", "500")
                },
            )
        }
        Row(
            Modifier.fillMaxWidth().flexWrap(org.jetbrains.compose.web.css.FlexWrap.Wrap)
                .gap(8.px).margin(top = 8.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            MapPill(AdminIcons.LocateFixed, AdminT.mapUseLocation, "#0F9D6E") { locateMe() }
        }
        SpanText(
            AdminT.mapPickHint,
            Modifier.fontSize(11.px).color(Brand.TextSecondary).margin(top = 6.px),
        )
    }
}

/** Rounded pill button used under the map. */
@Composable
private fun MapPill(iconSvg: String, label: String, iconColor: String, onClick: () -> Unit) {
    Row(
        Modifier
            .backgroundColor(Brand.WarmBackground)
            .border(1.px, LineStyle.Solid, Brand.Divider)
            .borderRadius(999.px)
            .padding(topBottom = 7.px, leftRight = 13.px)
            .cursor(Cursor.Pointer)
            .attrsModifier { classes("qk-press"); attr("role", "button") }
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        InlineIcon(iconSvg, 15, iconColor)
        SpanText(
            label,
            Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(Brand.Charcoal)
                .margin(left = 7.px).attrsModifier { classes("qk-nowrap") },
        )
    }
}

/** Build the map; the map centre is the picked point, reported on every pan/zoom. */
private fun initMap(lat: Double?, lng: Double?, onPick: (Double, Double) -> Unit) {
    val el = document.getElementById(MAP_ID) ?: return
    if (jsTypeOf(el.asDynamic()._leaflet_id) != "undefined") return // already initialised
    val leaflet = window.asDynamic().L
    if (jsTypeOf(leaflet) == "undefined") return

    val hasPoint = lat != null && lng != null
    // Saved point → zoom in on it; otherwise show the whole of Europe.
    val map = leaflet.map(MAP_ID, js("({zoomControl:true})"))
        .setView(arrayOf(lat ?: 54.0, lng ?: 15.0), if (hasPoint) 16 else 4)
    // Clean CARTO "Voyager" basemap; {r} pulls sharp retina tiles (no blur).
    leaflet.tileLayer(
        CARTO_VOYAGER_TILES,
        js("({maxZoom:20, subdomains:'abcd', attribution:'© OpenStreetMap, © CARTO'})"),
    ).addTo(map)

    map.on("moveend") {
        val center = map.getCenter()
        onPick(center.lat as Double, center.lng as Double)
    }

    el.asDynamic()._qkmap = map
    window.setTimeout({ map.invalidateSize() }, 200)
    window.setTimeout({ map.invalidateSize() }, 600)
}

/** Ask for geolocation permission on demand and recentre the map on the user's position. */
private fun locateMe() {
    val map = document.getElementById(MAP_ID)?.asDynamic()?._qkmap ?: return
    val geo = window.navigator.asDynamic().geolocation
    if (jsTypeOf(geo) == "undefined" || geo == null) return
    // maximumAge:0 forces a fresh permission/position request on every click.
    geo.getCurrentPosition(
        { pos: dynamic ->
            map.setView(arrayOf(pos.coords.latitude as Double, pos.coords.longitude as Double), 16)
        },
        { /* denied or unavailable — keep the current view */ },
        js("({enableHighAccuracy:true, timeout:10000, maximumAge:0})"),
    )
}
